var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React from 'react';
import { cx, css as emotionCss } from 'emotion';
import { iif, isNotNilOrEmpty, isNotNullOrEmpty, isNullOrEmpty } from '@pcty/utils';
import { disabled as disabledCss, disabledBorderless, flexed, fullWidth as fullWidthCss, iconOnly, iconOnlyBordered, large, largeBorderless, medium, mediumBorderless, processingBorderless, small, smallBorderless, responsive as responsiveCss, hoverSecondary, removeSiblingMargin as removeSiblingMarginLegacy, } from '../styles/legacy';
import { disabled as disabledCitron, disabledSecondary as disabledSecondaryCitron, disabledDefault as disabledDefaultCitron, disabledBorderless as disabledBorderlessCitron, iconOnly as iconOnlyCitron, iconOnlyBordered as iconOnlyBorderedCitron, small as smallCitron, medium as mediumCitron, mediumBorderless as mediumBorderlessCitron, large as largeCitron, largeBorderless as largeBorderlessCitron, processingBorderless as processingBorderlessCitron, selectedSecondary as selectedSecondaryCitron, smallBorderless as smallBorderlessCitron, removeSiblingMargin as removeSiblingMarginCitron, iconOnlyBorderless, smallBadge, smallBadgeButton, mediumBadgeButton, largeBadgeButton, smallBadgeSecondaryButton, } from '../styles/citron';
import { Badge as BadgePrimary, BadgeSecondary, BadgeDefault, BadgeBorderless, Borderless, BorderlessButtonLoader, BorderlessIcon, BorderlessNotification, BorderlessNotificationBlack, BorderlessNotificationBlue, BorderlessNotificationRed, BorderlessAs, BorderlessIconAs, ButtonAs as PrimaryAs, SecondaryAs, DefaultAs, HiddenText, NotificationAs, NotificationBlackAs, NotificationBlueAs, NotificationRedAs, BorderlessNotificationAs, BorderlessNotificationBlackAs, BorderlessNotificationBlueAs, BorderlessNotificationRedAs, Button as Primary, ButtonContainer, ButtonIconSmall, ButtonIconMedium, ButtonIconLarge, ButtonIconRight, ButtonLoader as PrimaryButtonLoader, ButtonText, DropdownIcon as DropdownIconPrimary, DropdownIconSecondary, DropdownIconDefault, Notification, NotificationBlack, NotificationBlue, NotificationRed, Secondary, SecondaryButtonLoader, DefaultButton, DefaultButtonLoader, } from './Button.styled';
import { PrimaryButton as PrimaryEEButton, PrimaryButtonAs as PrimaryEEButtonAs, SecondaryButton as SecondaryEEButton, SecondaryButtonAs as SecondaryEEButtonAs, PrimaryFloatingButton, PrimaryFloatingButtonAs, SecondaryFloatingButton, SecondaryFloatingButtonAs, } from '../ee/Button.styled';
import { getAutomation, getDebug, getStyleNames, useCitrusDebug } from '@citrus/runtime';
import { getThemeStyle, useLocalization, useTheme } from '@citrus/user';
import { getChildByType, removeChildrenByType } from 'react-nanny';
import localizationMap from '../localization';
var VERSION = '5.11.2';
var ButtonBase = React.forwardRef(function (_a, ref) {
    var _b, _c;
    var _d;
    var automationId = _a.automationId, as = _a.as, badge = _a.badge, buttonType = _a.buttonType, children = _a.children, className = _a.className, color = _a.color, css = _a.css, disabled = _a.disabled, disabledFocusable = _a.disabledFocusable, dropdown = _a.dropdown, fullWidth = _a.fullWidth, hidden = _a.hidden, onClick = _a.onClick, processing = _a.processing, _e = _a.removeSiblingMargin, removeSiblingMargin = _e === void 0 ? false : _e, responsive = _a.responsive, selected = _a.selected, size = _a.size, externalStyleNames = _a.styleNames, __flexed = _a.__flexed, TYPE = _a.__TYPE, other = __rest(_a, ["automationId", "as", "badge", "buttonType", "children", "className", "color", "css", "disabled", "disabledFocusable", "dropdown", "fullWidth", "hidden", "onClick", "processing", "removeSiblingMargin", "responsive", "selected", "size", "styleNames", "__flexed", "__TYPE"]);
    var lang = useLocalization(localizationMap);
    var debug = useCitrusDebug();
    var theme = useTheme();
    var isCitron = (_d = theme.citrus2022) !== null && _d !== void 0 ? _d : false;
    if (hidden)
        return null;
    var sizes = {
        small: TYPE === 'BorderlessButton'
            ? iif(isCitron, smallBorderlessCitron, smallBorderless)
            : getThemeStyle(theme, { legacy: small, citrus2022: smallCitron }),
        medium: TYPE === 'BorderlessButton'
            ? iif(isCitron, mediumBorderlessCitron, mediumBorderless)
            : getThemeStyle(theme, { legacy: medium, citrus2022: mediumCitron }),
        large: TYPE === 'BorderlessButton'
            ? iif(isCitron, largeBorderlessCitron, largeBorderless)
            : getThemeStyle(theme, { legacy: large, citrus2022: largeCitron }),
    };
    // Enforce Props
    badge = (TYPE === null || TYPE === void 0 ? void 0 : TYPE.indexOf('Notification')) === -1 ? badge : undefined;
    dropdown = ['PrimaryButton', 'SecondaryButton', 'DefaultButton'].indexOf(TYPE) !== -1 ? dropdown : false;
    fullWidth =
        ['PrimaryButton', 'SecondaryButton', 'DefaultButton', 'BorderlessButton'].indexOf(TYPE) !== -1
            ? fullWidth
            : false;
    processing = (TYPE === null || TYPE === void 0 ? void 0 : TYPE.indexOf('Notification')) === -1 ? processing : false;
    selected = TYPE === 'SecondaryButton' || TYPE === 'DefaultButton' ? selected : false;
    responsive =
        ['PrimaryButton', 'SecondaryButton', 'DefaultButton', 'BorderlessButton'].indexOf(TYPE) !== -1
            ? responsive
            : false;
    var icon = getChildByType(children, ['Icon', 'PctyIcon']);
    children = removeChildrenByType(children, ['Icon', 'PctyIcon']);
    var emotionDisabled;
    if ((TYPE === null || TYPE === void 0 ? void 0 : TYPE.indexOf('Borderless')) !== -1) {
        // All borderless
        emotionDisabled = isCitron ? disabledBorderlessCitron : disabledBorderless;
    }
    else if (TYPE === 'SecondaryButton') {
        // Secondary
        emotionDisabled = isCitron ? disabledSecondaryCitron : disabledCss;
    }
    else if (TYPE === 'DefaultButton') {
        // Secondary
        emotionDisabled = isCitron ? disabledDefaultCitron : disabledCss;
    }
    else {
        // Primary and everything else
        emotionDisabled = isCitron ? disabledCitron : disabledCss;
    }
    var emotion = {
        disabled: emotionCss(emotionDisabled),
        fullWidth: emotionCss(fullWidthCss),
        responsive: emotionCss(responsiveCss),
        css: emotionCss(css),
    };
    var borderlessIcon = icon && isNullOrEmpty(children) && TYPE === 'BorderlessButton';
    var emotionObj = (_b = {},
        _b[emotionCss(sizes[size])] = true,
        _b[emotionCss(isCitron ? iconOnlyBorderedCitron : iconOnlyBordered)] = icon && isNullOrEmpty(children),
        _b[emotion.disabled] = disabled || disabledFocusable,
        _b[emotionCss(isCitron ? processingBorderlessCitron : processingBorderless)] = TYPE === 'BorderlessButton' && processing,
        _b[emotion.fullWidth] = fullWidth,
        _b[emotionCss(isCitron ? selectedSecondaryCitron : hoverSecondary)] = selected,
        _b[emotion.responsive] = responsive,
        _b[emotionCss(flexed)] = __flexed,
        _b[emotionCss(isCitron ? removeSiblingMarginCitron : removeSiblingMarginLegacy)] = removeSiblingMargin,
        _b[emotionCss(iconOnlyBorderless)] = isCitron && borderlessIcon,
        _b[emotionCss(smallBadgeButton)] = isNotNilOrEmpty(badge) && size === 'small',
        _b[emotionCss(mediumBadgeButton)] = isNotNilOrEmpty(badge) && size === 'medium',
        _b[emotionCss(largeBadgeButton)] = isNotNilOrEmpty(badge) && size === 'large',
        _b[emotionCss(smallBadgeSecondaryButton)] = isNotNilOrEmpty(badge) && size === 'small' && TYPE === 'SecondaryButton',
        _b[emotion.css] = !!css,
        _b);
    var classes = cx(emotionObj, className || '', responsive ? 'citrus-button-responsive' : '');
    var styleNames = (_c = {
            smallBorderless: TYPE === 'BorderlessButton' && size === 'small',
            small: TYPE !== 'BorderlessButton' && size === 'small',
            mediumBorderless: TYPE === 'BorderlessButton' && size === 'medium',
            medium: TYPE !== 'BorderlessButton' && size === 'medium',
            largeBorderless: TYPE === 'BorderlessButton' && size === 'large',
            large: TYPE !== 'BorderlessButton' && size === 'large',
            iconOnlyBordered: emotionObj[emotionCss(isCitron ? iconOnlyBorderedCitron : iconOnlyBordered)]
        },
        _c[(TYPE === null || TYPE === void 0 ? void 0 : TYPE.indexOf('Borderless')) !== -1 ? 'disabledBorderless' : 'disabled'] = emotionObj[emotion.disabled],
        _c.processingBorderless = emotionObj[emotionCss(isCitron ? processingBorderlessCitron : processingBorderless)],
        _c.fullWidth = emotionObj[emotion.fullWidth],
        _c.hoverSecondary = emotionObj[emotionCss(hoverSecondary)],
        _c.responsive = emotionObj[emotion.responsive],
        _c.flexed = emotionObj[emotionCss(flexed)],
        _c);
    var text = renderText(children, fullWidth, emotion.fullWidth);
    var Components = getComponents(TYPE, color, icon, text, as);
    // other = removeKeys(other, ['removeSiblingMargin']);
    return (React.createElement(Components.Button, __assign({ "aria-busy": processing ? true : undefined, "aria-disabled": disabled || processing || disabledFocusable ? true : undefined, className: classes, disabled: disabled, onClick: disabled || processing || disabledFocusable ? undefined : onClick, ref: ref, styleNames: __spreadArray(__spreadArray([], getStyleNames(styleNames), true), (externalStyleNames !== null && externalStyleNames !== void 0 ? externalStyleNames : []), true) }, getAutomation(automationId), (buttonType ? { type: buttonType } : ''), other, getDebug(debug, TYPE, VERSION)),
        processing && React.createElement(HiddenText, null, "Loading, please wait."),
        React.createElement(ButtonContainer, { "aria-hidden": processing ? true : undefined },
            renderLoader(processing, Components.Loader),
            renderIcon(icon, badge, children, size, TYPE, isCitron),
            renderBadge(badge, Components.Badge, size),
            text,
            renderDropdown(dropdown, Components.DropdownIcon, lang))));
});
var getComponents = function (type, color, icon, text, as) {
    switch (type) {
        case 'PrimaryButton':
        default:
            return {
                Button: as ? PrimaryAs(as) : Primary,
                Badge: BadgePrimary,
                Loader: PrimaryButtonLoader,
                DropdownIcon: DropdownIconPrimary,
            };
        case 'SecondaryButton':
            return {
                Button: as ? SecondaryAs(as) : Secondary,
                Badge: BadgeSecondary,
                Loader: SecondaryButtonLoader,
                DropdownIcon: DropdownIconSecondary,
            };
        case 'DefaultButton':
            return {
                Button: as ? DefaultAs(as) : DefaultButton,
                Badge: BadgeDefault,
                Loader: DefaultButtonLoader,
                DropdownIcon: DropdownIconDefault,
            };
        case 'BorderlessButton': {
            var ButtonComponent = void 0;
            if (isNotNullOrEmpty(icon) && isNullOrEmpty(text)) {
                if (as) {
                    ButtonComponent = BorderlessIconAs(as);
                }
                else {
                    ButtonComponent = BorderlessIcon;
                }
            }
            else if (as) {
                ButtonComponent = BorderlessAs(as);
            }
            else {
                ButtonComponent = Borderless;
            }
            return {
                Button: ButtonComponent,
                Badge: BadgeBorderless,
                Loader: BorderlessButtonLoader,
            };
        }
        case 'NotificationButton':
            if (color === 'black') {
                return { Button: as ? NotificationBlackAs(as) : NotificationBlack };
            }
            if (color === 'blue') {
                return { Button: as ? NotificationBlueAs(as) : NotificationBlue };
            }
            if (color === 'red') {
                return { Button: as ? NotificationRedAs(as) : NotificationRed };
            }
            return { Button: as ? NotificationAs(as) : Notification };
        case 'BorderlessNotificationButton':
            if (color === 'black') {
                return { Button: as ? BorderlessNotificationBlackAs(as) : BorderlessNotificationBlack };
            }
            if (color === 'blue') {
                return { Button: as ? BorderlessNotificationBlueAs(as) : BorderlessNotificationBlue };
            }
            if (color === 'red') {
                return { Button: as ? BorderlessNotificationRedAs(as) : BorderlessNotificationRed };
            }
            return { Button: as ? BorderlessNotificationAs(as) : BorderlessNotification };
        case 'PrimaryEEButton':
            return {
                Button: as ? PrimaryEEButtonAs(as) : PrimaryEEButton,
            };
        case 'SecondaryEEButton':
            return {
                Button: as ? SecondaryEEButtonAs(as) : SecondaryEEButton,
            };
        case 'PrimaryFloatingButton':
            return {
                Button: as ? PrimaryFloatingButtonAs(as) : PrimaryFloatingButton,
            };
        case 'SecondaryFloatingButton':
            return {
                Button: as ? SecondaryFloatingButtonAs(as) : SecondaryFloatingButton,
            };
    }
};
var renderBadge = function (badge, Badge, size) {
    var _a;
    var badgeClasses = cx((_a = {},
        _a[emotionCss(smallBadge)] = size === 'small',
        _a));
    return isNotNilOrEmpty(badge) ? React.createElement(Badge, { className: badgeClasses }, badge) : undefined;
};
var renderLoader = function (processing, ButtonLoader) {
    return processing ? React.createElement(ButtonLoader, null) : undefined;
};
var renderIcon = function (icon, badge, children, size, type, isCitron) {
    var _a;
    if (badge || isNullOrEmpty(icon))
        return;
    var buttonSizeToIconSize = new Map([
        ['small', 'xsmall'],
        ['medium', 'small'],
        ['large', 'medium'],
    ]);
    var buttonSizeToIconSizeCitron = new Map([
        ['small', isNullOrEmpty(children) ? '12px' : '12px'],
        ['medium', isNullOrEmpty(children) ? '14px' : '12px'],
        ['large', isNullOrEmpty(children) ? '16px' : '12px'],
    ]);
    var buttonSizeToIconComponent = new Map([
        ['small', ButtonIconSmall],
        ['medium', ButtonIconMedium],
        ['large', ButtonIconLarge],
    ]);
    var iconBold = (_a = icon === null || icon === void 0 ? void 0 : icon.props) === null || _a === void 0 ? void 0 : _a.bold;
    var overrideBold = iconBold === false;
    var _boldicons = iconBold === true ||
        (!overrideBold && (isCitron || type === 'PrimaryFloatingButton' || type === 'SecondaryFloatingButton'));
    var _icon = React.cloneElement(icon, {
        bold: _boldicons,
        size: isCitron ? buttonSizeToIconSizeCitron.get(size) : buttonSizeToIconSize.get(size),
    });
    var _iconOnly = isCitron ? iconOnlyCitron : iconOnly;
    var ButtonIcon = buttonSizeToIconComponent.get(size);
    if ((icon && type === 'PrimaryFloatingButton') || type === 'SecondaryFloatingButton') {
        return React.createElement(ButtonIcon, null, _icon);
    }
    if (isNullOrEmpty(children)) {
        return React.createElement(ButtonIcon, { className: emotionCss(_iconOnly[size]) }, _icon);
    }
    return React.createElement(ButtonIcon, null, _icon);
};
var renderText = function (children, fullWidth, css) {
    var _a;
    if (isNullOrEmpty(children))
        return;
    var classes = cx((_a = {}, _a[css] = fullWidth, _a));
    return React.createElement(ButtonText, { className: classes }, children);
};
var renderDropdown = function (dropdown, DropdownIcon, lang) {
    if (!dropdown)
        return;
    return (React.createElement(ButtonIconRight, null,
        React.createElement(DropdownIcon, { "aria-label": lang.openMenu })));
};
ButtonBase.defaultProps = {
    buttonType: 'button',
    disabled: false,
    dropdown: false,
    fullWidth: false,
    hidden: false,
    processing: false,
    size: 'medium',
    __flexed: false,
    __TYPE: 'PrimaryButton',
};
export default ButtonBase;
